<script setup lang="ts">
import { ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import DimensionUnit = App.Enums.DimensionUnits;
import BucketUnits from "@/modules/dimension_values/components/BucketUnits.vue";
import DimensionDO = App.Data.DimensionDO;
import { useI18n } from "vue-i18n";
import { createBucketFromConfig } from "@/modules/dimension_values/components/BucketCreation";
import { ApiError, Result } from "@/api/base";
import { required, titleInputRules } from "@/ui/validation/validationRules";
import Switch from "@/ui/components/switch/switch.vue";
import Modal from "@/ui/components/modal.vue";
import { useCloned } from "@vueuse/core";

export type bucketCreationValues = {
	name: string;
	generate_from_clickhouse_data: boolean;
	start_value: number;
	item_count: number;
	gap_size: number;
};

const client = useApiClient("dimensions");
const { t } = useI18n();

const props = defineProps<{
	dimension: DimensionDO;
	bucketsCount: number;
}>();

const isOpen = defineModel<boolean>("isOpen");
const emit = defineEmits(["reload"]);

const isFormValid = ref(true);
const defaultBucketValues = ref<bucketCreationValues>({
	name: "",
	generate_from_clickhouse_data: false,
	item_count: null,
	start_value: 0,
	gap_size: 1,
});
const { cloned: newBucket, sync } = useCloned(defaultBucketValues, {
	manual: true,
});

watch(isOpen, (nv) => {
	if (nv) sync();
});

async function addAndReload() {
	let response: Result<any, ApiError>;

	if (newBucket.value.generate_from_clickhouse_data) {
		response = await toastApiResult(
			client.generateFromClickhouse(
				props.dimension.id,
				newBucket.value.name,
				newBucket.value.item_count
			)
		);
	} else {
		const values = createBucketFromConfig(newBucket.value, props.dimension, t);
		response = await toastApiResult(
			client.addBucket(props.dimension.id, {
				name: newBucket.value.name,
				values: values,
			})
		);
	}
	if (response.isOk()) {
		// reload after creation seems a bit wasteful, but it's fast enough to not be annoying
		emit("reload");
	}
}
</script>

<template>
	<modal
		v-model:visible="isOpen"
		v-model:allow-submit="isFormValid"
		title="Create Bucket"
		icon="fa-solid fa-plus"
		width="600px"
		@submit="addAndReload"
	>
		<v-form v-model="isFormValid">
			<div class="d-flex flex-column ga-4">
				<v-text-field
					v-model="newBucket.name"
					label="Bucket Name"
					hide-details="auto"
					:rules="titleInputRules(t)"
				/>
				<div class="d-flex align-center ga-1">
					<Switch
						v-model="newBucket.generate_from_clickhouse_data"
						label="Generate values from previous data"
						label-position="right"
					/>
					<v-tooltip>
						<template #activator="{ props }">
							<v-icon v-bind="props" size="18" icon="mdi-help-circle-outline" />
						</template>
						These values are created from the data from the last 365 days.<br />
						If this data is heavily clustered, some values may be missing or
						appear uneven.
					</v-tooltip>
				</div>

				<v-number-input
					v-model="newBucket.item_count"
					label="Amount of values"
					:min="2"
					:max="10_000"
					control-variant="stacked"
					inset
					:rules="[required(t)]"
					hide-details="auto"
				/>

				<v-slide-y-reverse-transition>
					<div v-if="!newBucket.generate_from_clickhouse_data">
						<v-number-input
							v-if="dimension.unit === 'default'"
							v-model="newBucket.start_value"
							label="Start value"
							:min="-100_000_000"
							:max="100_000_000"
							control-variant="stacked"
							inset
							hide-details="auto"
						/>
						<v-card
							v-else
							height="48"
							color="background"
							variant="flat"
							density="compact"
							class="d-flex flex-column bucket-unit-card"
						>
							<span> Start value </span>
							<bucket-units
								v-model:unit="props.dimension.unit as DimensionUnit"
								v-model:value="newBucket.start_value"
								:min="-100_000_000"
								:max="100_000_000"
							/>
						</v-card>
					</div>
				</v-slide-y-reverse-transition>

				<v-slide-y-reverse-transition>
					<div v-if="!newBucket.generate_from_clickhouse_data">
						<v-number-input
							v-if="dimension.unit === 'default'"
							v-model="newBucket.gap_size"
							label="Step size"
							:min="1"
							:max="10_000_000"
							control-variant="stacked"
							inset
							hide-details="auto"
						/>
						<v-card
							v-else
							height="48"
							density="compact"
							variant="flat"
							class="d-flex flex-column bucket-unit-card"
						>
							<span> Gap between values </span>
							<bucket-units
								v-model:unit="props.dimension.unit as DimensionUnit"
								v-model:value="newBucket.gap_size"
								:min="1"
								:max="10_000_000"
							/>
						</v-card>
					</div>
				</v-slide-y-reverse-transition>

				<v-slide-y-reverse-transition>
					<v-alert
						v-if="newBucket.item_count >= 100"
						title="Large buckets may slow down queries"
						type="warning"
					/>
				</v-slide-y-reverse-transition>
			</div>
		</v-form>
	</modal>
</template>

<style>
.bucket-unit-card {
	padding: 2px 16px;
	color: rgb(var(--color-primary));
	gap: -1px;
	font-size: 0.73rem;
	font-weight: 400;
	line-height: 1.25rem;
	letter-spacing: 0.03333em;
	border-radius: 0.25rem 0.25rem 0 0;
	border-bottom: 1px solid #979797;
	background-color: rgb(var(--color-background));
}
</style>
